// NEW_FILE_JUSTIFIED: Company hierarchy accordion for superadmin users table — unique component
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { UserCog, User, Users } from 'lucide-react';

// LOCAL_TYPE_JUSTIFIED: component props interface tightly coupled to this component
interface ICompanyHierarchyTreeProps {
  companyId: Id<'companies'>;
}

export function CompanyHierarchyTree({ companyId }: ICompanyHierarchyTreeProps) {
  const hierarchy = useQuery(api.superadmin.queries.getCompanyHierarchy, { companyId });

  if (hierarchy === undefined) {
    return (
      <div className="pl-8 py-2 space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-4 w-40" />
      </div>
    );
  }

  return (
    <div className="pl-8 py-3 bg-muted/30 border-t border-border">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3">
        <span>{hierarchy.companyName}</span>
        <Badge variant="outline" className="text-[10px]">{hierarchy.status}</Badge>
      </div>

      {hierarchy.members.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
            <UserCog className="w-3 h-3" />
            <span>Team Members ({hierarchy.members.length})</span>
          </div>
          <div className="space-y-1">
            {hierarchy.members.map(member => (
              <div key={member.userId} className="flex items-center gap-2 text-xs text-muted-foreground pl-5">
                <User className="w-3 h-3" />
                <span className="text-foreground">{member.name ?? member.email ?? 'Unknown'}</span>
                {member.roles.map(role => (
                  <Badge key={role} variant="secondary" className="text-[10px] px-1 py-0 capitalize">{role}</Badge>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {hierarchy.employeeCount > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
            <Users className="w-3 h-3" />
            <span>Employees ({hierarchy.employeeCount})</span>
          </div>
          <div className="space-y-1">
            {hierarchy.employees.map(emp => (
              <div key={emp._id} className="flex items-center gap-2 text-xs text-muted-foreground pl-5">
                <User className="w-3 h-3" />
                <span className="text-foreground">{emp.name ?? emp.email ?? 'Unknown'}</span>
                {emp.position && (
                  <span className="text-muted-foreground">{emp.position}</span>
                )}
              </div>
            ))}
            {hierarchy.employeeCount > hierarchy.employees.length && (
              <div className="text-[10px] text-muted-foreground pl-5">
                ...and {hierarchy.employeeCount - hierarchy.employees.length} more
              </div>
            )}
          </div>
        </div>
      )}

      {hierarchy.members.length === 0 && hierarchy.employeeCount === 0 && (
        <p className="text-xs text-muted-foreground">No team members or employees</p>
      )}
    </div>
  );
}
